import URI from "urijs";
import { Envs, UserLanguage } from "../@types";

export const getEnvironment = (): Envs => {
  const hostname = window.location.hostname;
  if (hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
    return Envs.local;
  }

  if (hostname.includes("test")) {
    return Envs.test;
  }

  if (hostname.includes("imp")) {
    return Envs.imp;
  }

  return Envs.prod;
};

export const parseDegradedIntegrations = (headers: Headers): string[] => {
  const integrations = headers.get("failed-integrations") || "";

  return integrations
    .trim()
    .split(",")
    .map(x => x.trim())
    .filter(x => !!x);
};

export const getEnrollURL = (): string => {
  const prefix = "https://";
  const base_url = "enrollmentcenter.medicare.gov/EnrollStart.aspx";

  const environment = getEnvironment();
  const envAddition =
    environment === Envs.local
      ? "test."
      : environment === Envs.prod
      ? ""
      : "imp.";

  return `${prefix}${envAddition}${base_url}`;
};

export enum SlsxUrlAction {
  AUTHORIZE = "authorize",
  SIGNOUT = "signout",
}

export const formatSlsxUrl = (env: Envs, action: SlsxUrlAction): string => {
  const templateUrl = "https://{env}account.mymedicare.gov/sso/{action}";

  const envUrlReplacements = {
    [Envs.local]: "imp1",
    [Envs.test]: "imp1",
    [Envs.imp]: "imp1",
    [Envs.prod]: "",
  };

  const urlReplacement = envUrlReplacements[env];

  const slsxUrl = templateUrl
    .replace("{env}", urlReplacement ? `${urlReplacement}.` : "")
    .replace("{action}", action);

  return slsxUrl;
};

export const getSlsxSignoutUrl = (): string => {
  const env = getEnvironment();

  const redirectUris = {
    [Envs.local]: "https://localhost",
    [Envs.test]: "https://test.medicare.gov/plan-compare",
    [Envs.imp]: "https://imp.medicare.gov/plan-compare",
    [Envs.prod]: "https://www.medicare.gov/plan-compare",
  };

  const redirectUri = redirectUris[env];
  const signoutUrl = new URI(formatSlsxUrl(env, SlsxUrlAction.SIGNOUT))
    .addQuery("redirect_uri", redirectUri)
    .toString();

  return signoutUrl;
};

export const getLoginParams = (
  env: Envs
): {
  client_id: string;
  mbp_uri: string;
  redirect_uri: string;
} => {
  const clientIds = {
    [Envs.local]: "mct-test",
    [Envs.test]: "mct-test",
    [Envs.imp]: "mct-imp",
    [Envs.prod]: "mct",
  };

  const client_id = clientIds[env];

  const mbp_uri = formatSlsxUrl(env, SlsxUrlAction.AUTHORIZE);
  let redirect_uri = "https://www.medicare.gov/plan-compare/sls-callback";

  if (env === Envs.local) {
    redirect_uri = "https://localhost/sls-callback";
  } else if (env !== Envs.prod) {
    redirect_uri = `https://${env}.medicare.gov/plan-compare/sls-callback`;
  }

  return {
    client_id,
    mbp_uri,
    redirect_uri,
  };
};

export const hasLoginSession = (): boolean | "" => {
  return document.cookie && document.cookie.includes("sid");
};

const buildLoginUrl = (
  isSpanish: boolean,
  redirectUri = "",
  relay: object = {}
): URI => {
  const env = getEnvironment();

  const { mbp_uri, ...paramsToSend } = getLoginParams(env);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  URI.escapeQuerySpace = false;

  let query: object = { ...paramsToSend };

  if (isSpanish) {
    query = { ...query, lang: "es-mx" };
  }

  if (redirectUri) {
    query = { ...query, redirect_uri: redirectUri };
  }

  const encoded = btoa(
    JSON.stringify({
      ...relay,
      lang: isSpanish ? UserLanguage.SPANISH : UserLanguage.ENGLISH,
    })
  );

  query = { ...query, relay: encoded };

  return new URI(mbp_uri).addQuery(query);
};

export const initiateLogin = (
  isSpanish: boolean,
  redirectUri = "",
  relay: object | undefined = undefined
): void => {
  const url = buildLoginUrl(isSpanish, redirectUri, relay);

  window.location.assign(url.toString());
};

export const getLoginUrl = (isSpanish: boolean): string => {
  const url = buildLoginUrl(isSpanish);

  return url.toString();
};

export const getRegistrationUrl = (isSpanish: boolean): string => {
  const env = getEnvironment();
  const prefix = env === Envs.local ? "imp2." : "";
  const baseURL = "account.mymedicare.gov/registration.aspx";
  const registrationURL = new URI(`https://${prefix}${baseURL}`);

  if (isSpanish) {
    registrationURL.addQuery("lang", "es-MX");
  }

  return registrationURL.toString();
};
